import type {
  CoachSessionPrepVeeraBrief,
  CoachVeeraPrepEvidenceRef,
} from "@/lib/behavior/viewModels/buildCoachSessionPrepVeeraBrief";

export interface CoachSessionPrepInterventionOption {
  id: string;
  title: string;
  purpose: string;
  tradeoff: string;
  watchout: string;
  evidenceRefs: CoachVeeraPrepEvidenceRef[];
  confidenceLabel: CoachSessionPrepVeeraBrief["confidenceLabel"];
  exploratory: boolean;
}

export interface CoachSessionPrepInterventionOptions {
  options: CoachSessionPrepInterventionOption[];
}

function trimSentence(value: string | null | undefined, fallback: string): string {
  const normalized = value?.replace(/\s+/g, " ").trim();
  return normalized && normalized.length > 0 ? normalized : fallback;
}

function takeEvidenceRefs(
  evidenceRefs: CoachVeeraPrepEvidenceRef[],
  start: number,
  count: number,
): CoachVeeraPrepEvidenceRef[] {
  const direct = evidenceRefs.slice(start, start + count);
  if (direct.length > 0) return direct;
  return evidenceRefs.slice(0, count);
}

function buildRichOptions(
  brief: CoachSessionPrepVeeraBrief,
): CoachSessionPrepInterventionOption[] {
  const tension = trimSentence(
    brief.unresolvedTension,
    brief.questionsToExplore[0] ?? brief.primaryFocus,
  );
  const shift = trimSentence(
    brief.whatChanged[0],
    brief.primaryFocus,
  );
  const persistentPattern = trimSentence(
    brief.whatPersists[0],
    brief.primaryFocus,
  );
  const sharedWatchout = trimSentence(
    brief.watchouts[0],
    "Do not turn an early read into a settled verdict before fresh live evidence shows up.",
  );

  return [
    {
      id: "probe-live-tension",
      title: "Probe the live tension",
      purpose: `Use the current unresolved tension to test ${tension}.`,
      tradeoff: "This may slow the session down before it sharpens the next move.",
      watchout: "Do not turn the tension into a verdict too early.",
      evidenceRefs: takeEvidenceRefs(brief.evidenceRefs, 0, 2),
      confidenceLabel: brief.confidenceLabel,
      exploratory: false,
    },
    {
      id: "reinforce-current-shift",
      title: "Reinforce the current shift",
      purpose: `Lean into the shift already showing up around ${shift}.`,
      tradeoff: "This can push the session toward action early and narrow what else still needs to surface.",
      watchout: "Do not reinforce progress so quickly that you miss whether it holds under pressure.",
      evidenceRefs: takeEvidenceRefs(brief.evidenceRefs, 1, 2),
      confidenceLabel: brief.confidenceLabel,
      exploratory: false,
    },
    {
      id: "hold-observe-pattern",
      title: "Hold and observe the pattern",
      purpose: `Stay with the repeated pattern long enough to see how ${persistentPattern} behaves under fresh pressure.`,
      tradeoff: "You may leave without a sharper behavioral commitment in the moment.",
      watchout: sharedWatchout,
      evidenceRefs: takeEvidenceRefs(brief.evidenceRefs, 2, 2),
      confidenceLabel: brief.confidenceLabel,
      exploratory: false,
    },
  ];
}

function buildExploratoryOptions(
  brief: CoachSessionPrepVeeraBrief,
): CoachSessionPrepInterventionOption[] {
  const exploratoryFocus = trimSentence(
    brief.primaryFocus,
    "the strongest emerging pattern",
  );
  const exploratoryQuestion = trimSentence(
    brief.questionsToExplore[0],
    "what is actually happening in the live moment",
  );

  return [
    {
      id: "test-emerging-read",
      title: "Test the emerging read",
      purpose: `Use the session to check whether ${exploratoryFocus} is real or only an early prep signal.`,
      tradeoff: "You may spend more time clarifying the situation before you land on a concrete move.",
      watchout: "Keep the language tentative and ask for a live example before naming a pattern too strongly.",
      evidenceRefs: takeEvidenceRefs(brief.evidenceRefs, 0, 1),
      confidenceLabel: "low",
      exploratory: true,
    },
    {
      id: "invite-one-concrete-example",
      title: "Invite one concrete example",
      purpose: `Anchor the conversation in one fresh example that helps test ${exploratoryQuestion}.`,
      tradeoff: "A concrete example may narrow the conversation before the broader pattern is visible.",
      watchout: "Do not confuse one recent example with a stable behavioral arc.",
      evidenceRefs: takeEvidenceRefs(brief.evidenceRefs, 1, 1),
      confidenceLabel: "low",
      exploratory: true,
    },
  ];
}

export function buildCoachSessionPrepInterventionOptions(params: {
  brief: CoachSessionPrepVeeraBrief;
}): CoachSessionPrepInterventionOptions {
  const { brief } = params;
  const hasRichEvidence =
    brief.confidenceLabel !== "low"
    && brief.evidenceRefs.length > 0
    && (brief.whatChanged.length > 0 || brief.whatPersists.length > 0 || Boolean(brief.unresolvedTension));

  return {
    options: hasRichEvidence
      ? buildRichOptions(brief)
      : buildExploratoryOptions(brief),
  };
}
